import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
} from '@acceso/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TableWithDetailCard } from '@/components/TableWithDetailCard/TableWithDetailCard';
import { FormTypeEnum } from '@/types';
import { ParamsWithPagination } from '@/types/api';

import { useCNMCs } from '../api/get-cnmcs';
import { CNMCDetailCard } from '../components/CNMCDetailCard/CNMCDetailCard';
import { CNMCDrawerForm } from '../components/CNMCDrawerForm/CNMCDrawerForm';
import { CNMCFilesUploadDrawer } from '../components/CNMCFilesUploadDrawer/CNMCFilesUploadDrawer';
import { CNMCsTable } from '../components/CNMCsTable/CNMCsTable';
import { useCNMCControllers } from '../hooks/useCNMCControllers';
import { useCNMCPermissions } from '../hooks/useCNMCPermissions';
import { CNMC, CNMCFilters } from '../types/index';

export default function CNMCsPage() {
  const { t } = useTranslation();
  const [paginationParams, setPaginationParams] = useState<ParamsWithPagination<CNMCFilters>>({
    limit: 10,
    offset: 0,
  });

  const {
    data: { data: items = [], total = 0 } = {},
    isFetching,
    isError,
  } = useCNMCs({ params: paginationParams });

  const {
    cnmcDialog,
    cnmcDrawer,
    cnmcFilesUploadDrawer,
    handleDeleteCNMC,
    handleCNMCSubmit,
    isMutating,
  } = useCNMCControllers();

  const { canCreate, canUpdate, canDelete } = useCNMCPermissions();

  const onCreate = () => {
    if (!canCreate) return;
    cnmcDrawer.open({ type: FormTypeEnum.CREATE, data: null });
  };

  const onUpdate = (cnmc: CNMC) => {
    if (!canUpdate) return;
    cnmcDrawer.open({ type: FormTypeEnum.UPDATE, data: cnmc });
  };

  const onDelete = (cnmc: CNMC) => {
    if (!canDelete) return;
    cnmcDialog.open(cnmc);
  };

  const handlePaginationChange = ({ limit, offset }: { limit: number; offset: number }) => {
    setPaginationParams(prevParams => ({
      ...prevParams,
      limit,
      offset,
    }));
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Informes CNMC')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nueva'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <TableWithDetailCard<CNMC>
          isError={isError}
          renderTable={({ handleOpenSecondCard, data }) => (
            <CNMCsTable
              items={items}
              totalItems={total}
              isFetching={isFetching}
              activeItem={data}
              onSelect={handleOpenSecondCard}
              onUpdate={canUpdate ? onUpdate : undefined}
              onDelete={canDelete ? onDelete : undefined}
              onFilesUpload={cnmc =>
                cnmcFilesUploadDrawer.open({
                  data: cnmc,
                  type: FormTypeEnum.CREATE,
                })
              }
              onPaginationChange={handlePaginationChange}
              paginationValues={{
                currentPage: Math.floor(paginationParams.offset! / paginationParams.limit!) + 1,
                itemsPerPage: paginationParams.limit!,
              }}
            />
          )}
          renderDetailCard={({ data, isSecondCardCollapsed, handleSecondCardCollapse }) => (
            <CNMCDetailCard
              cnmc={data}
              isCollapsed={isSecondCardCollapsed}
              onCollapse={handleSecondCardCollapse}
            />
          )}
        />
      </div>

      <CNMCDrawerForm
        isOpen={cnmcDrawer.isOpen}
        onClose={cnmcDrawer.close}
        onSubmit={handleCNMCSubmit}
        isMutating={isMutating}
        data={cnmcDrawer.data}
      />

      <CNMCFilesUploadDrawer
        isOpen={cnmcFilesUploadDrawer.isOpen}
        onClose={cnmcFilesUploadDrawer.close}
        onSubmit={() => {}}
        isMutating={false}
        data={cnmcFilesUploadDrawer.data}
      />

      <ModalDialog
        isOpen={cnmcDialog.isOpen}
        onClose={cnmcDialog.close}
        onAction={handleDeleteCNMC}
        heading={t('Eliminar proceso')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t('¿Estás seguro de querer eliminar el proceso?')}
          description={t(
            'Es una acción irreversible, una vez eliminada no podrá recuperarlo. Se eliminarán los datos del proceso y los archivos relacionados'
          )}
        />
      </ModalDialog>
    </>
  );
}
